import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/7d243b86/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const InlineNotification = makeShortcode("InlineNotification");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const Caption = makeShortcode("Caption");
const DoDontExample = makeShortcode("DoDontExample");
const Video = makeShortcode("Video");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Under the hood</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Getting started</AnchorLink>
      <AnchorLink mdxType="AnchorLink">How it works</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Grid modes</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Incorporating the shell</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Alignment</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Techniques</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "under-the-hood"
    }}>{`Under the hood`}</h2>
    <p>{`The IBM 2x Grid system is a fully responsive, robust, 16 column layout system with five default breakpoints and dozens of predefined classes.`}</p>
    <p>{`Our grid is built with Flexbox and closely mirrors Bootstrap’s grid in that it uses containers, rows and columns to layout and align content. Below is an in-depth look at how it all comes together.`}</p>
    <p>{`If you’re new to Flexbox, this `}<a parentName="p" {...{
        "href": "https://css-tricks.com/snippets/css/a-guide-to-flexbox/"
      }}>{`CSS Tricks Flexbox guide`}</a>{` will provide more detail around everything from basic terminology to code.`}</p>
    <h2 {...{
      "id": "getting-started"
    }}>{`Getting started`}</h2>
    <h3 {...{
      "id": "with-carbon-components"
    }}>{`With Carbon-components`}</h3>
    <p>{`We provide multiple ways for you to use the grid for the Carbon Design System. By default, the grid code is included when you use `}<inlineCode parentName="p">{`carbon-components`}</inlineCode>{`. To get started, you’ll need to install this package using `}<inlineCode parentName="p">{`npm`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install carbon-components --save
`}</code></pre>
    <br />
    <p>{`Or, if you prefer Yarn:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`yarn add carbon-components
`}</code></pre>
    <br />
    <p>{`You can include the grid in one of two ways:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss"
      }}>{`// If you're including all the styles for Carbon
@import 'carbon-components/scss/globals/scss/styles.scss';

// If you're wanting to import the grid
@import 'carbon-components/scss/globals/grid/grid';
`}</code></pre>
    <br />
    <p>{`To use 16 column variant of the grid, you need to enable the `}<inlineCode parentName="p">{`grid-columns-16`}</inlineCode>{` feature flag before including any imports in your Sass files. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss"
      }}>{`$feature-flags: (
  grid-columns-16: true,
);

// If you're including all the styles for Carbon
@import 'carbon-components/scss/globals/scss/styles.scss';

// If you want to import the grid
@import 'carbon-components/scss/globals/grid/grid';
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` By default, Carbon loads with 12 columns. In a later release this will change and the grid will default to 16 columns. Until then we ask you to enable the feature flag.`}</p>
    </InlineNotification>
    <h3 {...{
      "id": "without-carbon-components"
    }}>{`Without Carbon-components`}</h3>
    <p>{`If you’d like to use the grid without including `}<inlineCode parentName="p">{`carbon-components`}</inlineCode>{`, we offer the `}<inlineCode parentName="p">{`@carbon/grid`}</inlineCode>{` package. You can install this with `}<inlineCode parentName="p">{`npm`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install @carbon/grid --save
`}</code></pre>
    <br />
    <p>{`Or, if you prefer Yarn:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`yarn add @carbon/grid
`}</code></pre>
    <br />
    <p>{`You can then include the 16 column grid by doing the following in your Sass files:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss"
      }}>{`@import '@carbon/grid/scss/grid';
`}</code></pre>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`We won’t spend a lot of time here recapping the basics of the `}<a parentName="p" {...{
        "href": "https://getbootstrap.com/docs/4.0/layout/grid/"
      }}>{`Bootstrap grid`}</a>{`. Instead we’ll emphasize the features that make this system unique, especially concerning gutter structure, type alignment, and the UI Shell.`}</p>
    <h3 {...{
      "id": "basic-grid-explainer"
    }}>{`Basic grid explainer`}</h3>
    <p>{`The below example creates four equal-width columns at small, medium, large, and extra large breakpoints using our predefined grid classes. The columns are centered in the page within the parent container.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">2 of 4</div>
          <div className="bx-demo--col">3 of 4</div>
          <div className="bx-demo--col">4 of 4</div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid">
  <div class="bx--row">
    <div class="bx--col">1 of 4</div>
    <div class="bx--col">2 of 4</div>
    <div class="bx--col">3 of 4</div>
    <div class="bx--col">4 of 4</div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "container"
    }}>{`Container`}</h4>
    <p><inlineCode parentName="p">{`bx--grid`}</inlineCode>{` defines the overall grid context at the top level and applies the proper margin and our default max-width (1584px.) You can also remove this max width, by using a grid modifier class. See the full width example below.`}</p>
    <h4 {...{
      "id": "rows"
    }}>{`Rows`}</h4>
    <p><inlineCode parentName="p">{`bx--row`}</inlineCode>{` defines a row of items inside the grid — think of rows as wrappers for columns. Each column has horizontal padding (aka a gutter) that defines the space between them. You can negate this padding at the row level with negative margins, allowing the content within your columns to be aligned along the left side.`}</p>
    <h4 {...{
      "id": "columns"
    }}>{`Columns`}</h4>
    <p><inlineCode parentName="p">{`bx--col`}</inlineCode>{` defines individual columns inside a row. Use as many columns as you’d like out of the possible 16 per row. With Flexbox columns widths are set in percentages relative to their parent element so columns without a spec’d width will automatically be equal. For example, four instances of `}<inlineCode parentName="p">{`bx--col`}</inlineCode>{` will will give you four equal width columns — each 25% of the parent from the small breakpoint on up. See the auto-layout columns for more on this.`}</p>
    <h3 {...{
      "id": "responsive-options"
    }}>{`Responsive options`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Breakpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Class prefix`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`# of cols`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Gutter width`}{`*`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Small (<320)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.bx--col-sm-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Medium (≥675)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.bx--col-md-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`8`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Large (≥1056)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.bx--col-lg-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`16`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`X-Large (≥1312)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.bx--col-xl-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`16`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Max (≥1584)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`.bx--col-max-`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`16`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`32px`}</td>
        </tr>
      </tbody>
    </table>
    <Caption mdxType="Caption">
  * Gutter width is 32px — with 16px on each side of a column — at every
  breakpoint.
    </Caption>
    <br /><br />
    <h4 {...{
      "id": "responsive-classes"
    }}>{`Responsive classes`}</h4>
    <p>{`Here’s a look at all the width classes available for our 16 column grid. Use the column helpers to specify different column spans at different widths.`}</p>
    <p>{`If you want to change it up, use a combination of different classes for each grid tier as needed. See the markup below. Note: 3 and 5 column arrangements are discouraged, however, in specific instances, you are allowed to generate a three columns within our system.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-16">
            <div className="bx-demo-lg">16</div>
            <div className="bx-demo-md">8</div>
            <div className="bx-demo-sm">4</div>
          </div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-15 bx-demo--col-md-7 bx-demo--col-sm-3">
            <div className="bx-demo-lg">15</div>
            <div className="bx-demo-md">7</div>
            <div className="bx-demo-sm">3</div>
          </div>
          <div className="bx-demo--col-sm-1 bx-demo--col-md-1 bx-demo--col-lg-1">1</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-14 bx-demo--col-md-6 hide-sm">
            <div className="bx-demo-lg">14</div>
            <div className="bx-demo-md">6</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-2 hide-sm">2</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-13 bx-demo--col-md-5 hide-sm">
            <div className="bx-demo-lg">13</div>
            <div className="bx-demo-md">5</div>
          </div>
          <div className="bx-demo--col-lg-3 bx-demo--col-md-3 hide-sm">3</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-12 hide-md">12</div>
          <div className="bx-demo--col-lg-4 hide-md">4</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-11 hide-md">11</div>
          <div className="bx-demo--col-lg-5 hide-md">5</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-10 hide-md">10</div>
          <div className="bx-demo--col-lg-6 hide-md">6</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-9 hide-md">9</div>
          <div className="bx-demo--col-lg-7 hide-md">7</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-8 bx-demo--col-md-4 bx-demo--col-sm-2">
            <div className="bx-demo-lg">8</div>
            <div className="bx-demo-md">4</div>
            <div className="bx-demo-sm">2</div>
          </div>
          <div className="bx-demo--col-lg-8 bx-demo--col-md-4 bx-demo--col-sm-2">
            <div className="bx-demo-lg">8</div>
            <div className="bx-demo-md">4</div>
            <div className="bx-demo-sm">2</div>
          </div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-4 bx-demo--col-md-2 bx-demo--col-sm-1">
            <div className="bx-demo-lg">4</div>
            <div className="bx-demo-md">2</div>
            <div className="bx-demo-sm">1</div>
          </div>
          <div className="bx-demo--col-lg-4 bx-demo--col-md-2 bx-demo--col-sm-1">
            <div className="bx-demo-lg">4</div>
            <div className="bx-demo-md">2</div>
            <div className="bx-demo-sm">1</div>
          </div>
          <div className="bx-demo--col-lg-4 bx-demo--col-md-2 bx-demo--col-sm-1">
            <div className="bx-demo-lg">4</div>
            <div className="bx-demo-md">2</div>
            <div className="bx-demo-sm">1</div>
          </div>
          <div className="bx-demo--col-lg-4 bx-demo--col-md-2 bx-demo--col-sm-1">
            <div className="bx-demo-lg">4</div>
            <div className="bx-demo-md">2</div>
            <div className="bx-demo-sm">1</div>
          </div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
         <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
          <div className="bx-demo--col-lg-2 bx-demo--col-md-1 hide-sm">
            <div className="bx-demo-lg">2</div>
            <div className="bx-demo-md">1</div>
          </div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
          <div className="bx-demo--col-lg-1 hide-md">1</div>
        </div>
    </div>
  </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid">
  <div class="bx--row">
    <div class="bx--col-lg-16">16</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-15 bx--col-md-7 bx--col-sm-3">15</div>
    <div class="bx--col-sm-1 bx--col-md-1 bx--col-lg-1">1</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-14 bx--col-md-6">14</div>
    <div class="bx--col-lg-2 bx--col-md-2">2</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-13 bx--col-md-5">13</div>
    <div class="bx--col-lg-3 bx--col-md-3">3</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-12">12</div>
    <div class="bx--col-lg-4">4</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-11">11</div>
    <div class="bx--col-lg-5">5</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-10">10</div>
    <div class="bx--col-lg-6">6</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-9">9</div>
    <div class="bx--col-lg-7">7</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-8 bx--col-md-4 bx--col-sm-2">8</div>
    <div class="bx--col-lg-8 bx--col-md-4 bx--col-sm-2">8</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-4 bx--col-md-2 bx--col-sm-1">4</div>
    <div class="bx--col-lg-4 bx--col-md-2 bx--col-sm-1">4</div>
    <div class="bx--col-lg-4 bx--col-md-2 bx--col-sm-1">4</div>
    <div class="bx--col-lg-4 bx--col-md-2 bx--col-sm-1">4</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
    <div class="bx--col-lg-2 bx--col-md-1">2</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
    <div class="bx--col-lg-1">1</div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do feel free to mix and match column structure." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABTElEQVQ4y82Uy4qDQBBF8/9fkChEBFfxP9SNWRmN72dAvyCJhlS4Ba02msUMDoxwpLqr+vat9rGjja/dnwi+3+9NYEERbHFBa3T4er2o7/tfMQzD5BA3TJqmSZqmka7rP+J41Oh0OtHj8ZgEn88nJw+HA6mqukBRFGZtvN/vyTAMut/vkyDadV2XLMsix3HItm0JzM3nxRhgzfl85ralh9K2LcVxTGmaSiRJQlVVMciDuq6pKAqOoyjiteNDEYJY4HkeBUFAvu+PXC4XFs3znMIwZCCWZRnH1+uVmqZZCmISAiiYgw3gFHmIAsRwiRjCqw5RADdCRIBNROvIA4zRKmJ0he4Wgl3X8Y5lWXKBOCeAGCAH5mPksfarQ7jC7igURzB3PAc5rFk9Q+yCVoTL2+3G5yPO7RuokRyKYIvvWHoPt/jTSC1vJvjv/9gfiwDc+5V/6W8AAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do feel free to mix and match column structure.",
              "title": "Do feel free to mix and match column structure.",
              "src": "/static/e1df83422ca19a9d7031e34204f44262/9eefd/Responsive_classes_do.png",
              "srcSet": ["/static/e1df83422ca19a9d7031e34204f44262/d6747/Responsive_classes_do.png 288w", "/static/e1df83422ca19a9d7031e34204f44262/09548/Responsive_classes_do.png 576w", "/static/e1df83422ca19a9d7031e34204f44262/9eefd/Responsive_classes_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="As a general rule, don’t mix with 3 or 5 columns." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABVUlEQVQ4y81Uy4qDQBDM//+BUXMwN/FHYgIejJqHmkTwgfEUMCTkUUsNO0ZxYbNgYAeami66q7tndEYYeI0+Ivh8PgcxISg3QyxqNR1eLhfUdY3z+fwnkzmPx+M18v1+h2VZUBQFmqZB/bb2/ieO+/F4DMMwcDqdXoK32w2maUJVVUwmE+i6/pYxlqLT6bQryJGXyyVmsxnm8zls2xa4WCwaX1qbIzLHcRxcr9fupRyPR2y3W8RxjCiKBGZZ1vHDMESe59jtdh2Occ2lSME0TeG6LoIggO/7ApkofZrneYJbrVYNx8nI9T4bdsiKh8MB+/1eICtLX1qbI1KMXfcEGciK6/VadEBkQttnt+Q2m02P6wmyym+CxLcFy7IUB80RODqR59r2ieQ4ruSYQ64nWFWVCGA1aey67VOoKAokSdI5Q3KNoLzuwf5lKTb4azOY4L9/sb8A22bdpsDEe0cAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "As a general rule, don’t mix with 3 or 5 columns.",
              "title": "As a general rule, don’t mix with 3 or 5 columns.",
              "src": "/static/7d7dacaf4bbb4bb71a3d419235e72b5c/9eefd/Responsive_classes_dont.png",
              "srcSet": ["/static/7d7dacaf4bbb4bb71a3d419235e72b5c/d6747/Responsive_classes_dont.png 288w", "/static/7d7dacaf4bbb4bb71a3d419235e72b5c/09548/Responsive_classes_dont.png 576w", "/static/7d7dacaf4bbb4bb71a3d419235e72b5c/9eefd/Responsive_classes_dont.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h4 {...{
      "id": "equal-width-columns"
    }}>{`Equal width columns`}</h4>
    <p>{`Use these classes for easy column sizing when you want to maintain the same grid tiers at every breakpoint. In the example below, the grid layouts apply to every breakpoint: from `}<inlineCode parentName="p">{`sm`}</inlineCode>{` to `}<inlineCode parentName="p">{`max`}</inlineCode>{`. Add any number of unit-less classes per row you need and every column will be the same width.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
          <div className="bx-demo--col hide-md">1 of 8</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 2</div>
          <div className="bx-demo--col">1 of 2</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 1</div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid">
  <div class="bx--row">
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
    <div class="bx--col">1 of 8</div>
  </div>
  <div class="bx--row">
    <div class="bx--col">1 of 4</div>
    <div class="bx--col">1 of 4</div>
    <div class="bx--col">1 of 4</div>
    <div class="bx--col">1 of 4</div>
  </div>
  <div class="bx--row">
    <div class="bx--col">1 of 2</div>
    <div class="bx--col">1 of 2</div>
  </div>
  <div class="bx--row">
    <div class="bx--col">1 of 1</div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <h2 {...{
      "id": "grid-modes"
    }}>{`Grid modes`}</h2>
    <p>{`Carbon’s Sketch templates always have a 32px gutter by default, regardless of breakpoint. The default gutter width is 32px because type, within or without containers, never has less than a 32px gutter. For containers and components, however, there are three different gutter structures: Wide (default), Narrow and Condensed. These three gutter scenarios enable typographic alignment by allowing containers and certain components to “hang” into the gutter.`}</p>
    <br /><br />
    <Row className="grid-modes" mdxType="Row">
      <Column colLg={2} colMd={2} mdxType="Column">
        <p><strong parentName="p">{`Wide (default)`}</strong><br />{`
32px gutter`}</p>
      </Column>
      <Column colLg={10} colMd={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "11.281489594742608%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAACCAYAAABYBvyLAAAACXBIWXMAAAsSAAALEgHS3X78AAAAdElEQVQI1y2MwQqCQBCG9/1fJOjiExheBAnxkAcT1kpYs3aVzLx/7jAdfmaY+f7PNCP4FYYPuJhpg6KDpIKshbSBqof3nxkW5cIPXl94LnqTv7hM7eAxQ+fBei3lFg5nOJY6T1eV3ALcJ2VF1EsvaGS/RNcOVaiVfOzrcAgAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Wide grid mode",
            "title": "Wide grid mode",
            "src": "/static/36248035736a2c2144149f0c1f6320d2/fb070/Wide.png",
            "srcSet": ["/static/36248035736a2c2144149f0c1f6320d2/d6747/Wide.png 288w", "/static/36248035736a2c2144149f0c1f6320d2/09548/Wide.png 576w", "/static/36248035736a2c2144149f0c1f6320d2/fb070/Wide.png 1152w", "/static/36248035736a2c2144149f0c1f6320d2/fb104/Wide.png 1728w", "/static/36248035736a2c2144149f0c1f6320d2/4579f/Wide.png 1826w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
      <Column colLg={2} colMd={2} mdxType="Column">
        <p><strong parentName="p">{`Narrow`}</strong><br />{`
16px gutter`}</p>
      </Column>
      <Column colLg={10} colMd={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "11.281489594742608%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAACCAYAAABYBvyLAAAACXBIWXMAAAsSAAALEgHS3X78AAAAkUlEQVQI10WNOQoCQRAA94GC+A0x8wci/sBMMDDzAV6gIiYeILIIi4qIrCbuIc7szoRlzyQGFVR30R0M8oyTtWzKkq1wtIb2M6Z+u9J5xbSEXppwMIa9KT2uC+3fd94tw09O0E3erArNSH0ZC3OtaDzuVM8RtUtERWjKg4XWTGQ/Vcp366JgJq1zN1/KjX6W8gPAAo0TdfHiGwAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Narrow grid mode",
            "title": "Narrow grid mode",
            "src": "/static/2792ffcf3454c011a961bc9be3a6baed/fb070/Narrow.png",
            "srcSet": ["/static/2792ffcf3454c011a961bc9be3a6baed/d6747/Narrow.png 288w", "/static/2792ffcf3454c011a961bc9be3a6baed/09548/Narrow.png 576w", "/static/2792ffcf3454c011a961bc9be3a6baed/fb070/Narrow.png 1152w", "/static/2792ffcf3454c011a961bc9be3a6baed/fb104/Narrow.png 1728w", "/static/2792ffcf3454c011a961bc9be3a6baed/4579f/Narrow.png 1826w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
      <Column colLg={2} colMd={2} mdxType="Column">
        <p><strong parentName="p">{`Condensed`}</strong><br />{`
2px gutter`}</p>
      </Column>
      <Column colLg={10} colMd={6} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "11.281489594742608%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAACCAYAAABYBvyLAAAACXBIWXMAAAsSAAALEgHS3X78AAAAdklEQVQI1y2MOxKCUBAEuf819AJiLmYSooElCQnyKPH35FFmTrsrBlPVWzPbWTjCO0IaRLqJ6Q71VuzX4lRYNqIpxXjBOt/Y9spv5zz9b3PIXdn5AOMAz07E4IEqF7vFh3LpEdVKPFp49XMfO2dmDv6LkjnMpS99H5X6N7yqCgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Condensed grid mode",
            "title": "Condensed grid mode",
            "src": "/static/7668561e10359582ca446aa7464a04e4/fb070/Condensed.png",
            "srcSet": ["/static/7668561e10359582ca446aa7464a04e4/d6747/Condensed.png 288w", "/static/7668561e10359582ca446aa7464a04e4/09548/Condensed.png 576w", "/static/7668561e10359582ca446aa7464a04e4/fb070/Condensed.png 1152w", "/static/7668561e10359582ca446aa7464a04e4/fb104/Condensed.png 1728w", "/static/7668561e10359582ca446aa7464a04e4/4579f/Condensed.png 1826w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h3 {...{
      "id": "the-wide-grid"
    }}>{`The Wide grid`}</h3>
    <p>{`This is our default grid mode, with 16 columns and 32px gutters. Unless your design specifies something different, this is the grid you should go with — it’s also arguably the easiest to use. Here, the container edge does not extend into the gutter, so the type within the container does not sit on the columns.`}</p>
    <p>{`Rows that are adjacent will have 32px of margin between them to match gutter. Use vertical helpers here.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo grid-demo--wide">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid">
  <div class="bx--row">
    <div class="bx--col"></div>
    <div class="bx--col"></div>
    <div class="bx--col"></div>
    <div class="bx--col"></div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "usage"
    }}>{`Usage`}</h4>
    <p>{`Use the Wide grid mode when you are dealing with separate pieces of information with separate destinations. This grid mode is also good for text-heavy situations, where maximum breathing room helps provide clarity and legibility. Also, basic images (without text on them) always use this grid mode.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Fixed components with labels such as input fields and dropdowns must use the Wide grid mode. One overarching rule — applicable to each grid mode — is that type never hangs into the gutter. So we don’t want to push label type into the gutter in order to get field type to align.`}</p>
    </InlineNotification>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAChklEQVQ4y61T2U5TURTtH0hxeJdBRP0EffAbNH6HJppY4ktleuQb+AgTE0kIIK1Aa7WtElDK0IGWO4+941mue4rRGB69yc6+d99z1l5r7XNy+M9PLk1TrK6uolgsYnFxUUax+BaF1wXMFeYwPz+PhYUFLC0tXRnLy8tyz8rKCnRdRy6KIjx5+gwTU3dw994DTM/M4uGjx3j5qojnL95gZvY+bk9M4cbNW7g2lsdYfnwUY6Ocz19nfRyTk9NotVrIZTS/NA+wtrmH0l4DG6Uaqo0fUOwYmpPic+MnKvVDlCtNbO/WGQ25LvsuMcp8/7hTx061iSAIRoCaNcRRW8e56mNgRDKf9Cy0By4UK4Fqp9BdAcPlWkfgworQuXBwrg1heAIqG+tOAkH7CCgk4LkewAgEWAfXwE0h37OsDgErEjBDwQwc9ww0D89w3NXZWMf+UQ9nfRNpmhBQpDC9iEyA0ocIjUqEr+UItVKIZpV5O0R5LUJlk7EVYb8eo6d6aHU0Ms/Yx+gbIVR2SpLfgG6IvpZg652BbzWfmx1utgnuYWfdxvZ7E5UNG7vrFmqfXPRNWnSmSkDdTWgB/SbwCJCSdTvAwIyhewlsSnPiTF4qg+tgDFNKTZkJ4KfoKC4916gqlgwvuFexwj+AiuHRDwN9fQhF/oyk4accjGKGBElkrd230dN8CTIwAgKFsj6wSEj3Lz2UgEN0tUBOS+M0Vfo5MGIOKkaznaJKmQNa0lEcnHRddPsxuvTxlA26ZPv9QEOHJyIVl1N2vAA2WXgBpbkxbPri+PSFIyZJdI6HaJ/4cEMOj417bZ4Ih0ypTDd9ek0/qS7Dygnx900U/9xMcUUtK4kr73FW/gUyaZw7T+H+uQAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "32px gutter",
        "title": "32px gutter",
        "src": "/static/dd1ae0167ca568065c7e5f006ec347ea/fb070/32px_gutter.png",
        "srcSet": ["/static/dd1ae0167ca568065c7e5f006ec347ea/d6747/32px_gutter.png 288w", "/static/dd1ae0167ca568065c7e5f006ec347ea/09548/32px_gutter.png 576w", "/static/dd1ae0167ca568065c7e5f006ec347ea/fb070/32px_gutter.png 1152w", "/static/dd1ae0167ca568065c7e5f006ec347ea/fb104/32px_gutter.png 1728w", "/static/dd1ae0167ca568065c7e5f006ec347ea/8fefe/32px_gutter.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do align type (outside of a container), components, and tiles to the columns." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADbUlEQVQ4y62T224bZRSFjRoHeC4eACEh8QCIC65QrogBCV6BCiEukCqBUKMCaqS0dnOyc3LcNLGbk5Mmjh18HI/PnrNnHH/sGSttbCFu2pG2Zvm3/jVr771WCHlGoxG377et0F3Cm5sbPM97qwoIb8nexRMQDofD4MfOzg5zc3NEvvmW+UiEiNTX85EJ7L/n/wcHhK7rBoT37/9IKBQiPPsBMzNhZsJh3rsXfo3vzbzP7Oy4bnF4Ck/MUKnViMbXeJpIk3yeYW8/QzSRCXBqL83j2C6JzSRxqcexJBtbSdY3xnhzO8laIvmG8Jb0SimzsJRgZX2beGKLP59s8Wx1Uy7vkqvaGPYNmuVRUIeY9pC+OeRasOUM6RnDSYVD2dLZPwWW4vuBqv30IbGNQ1IvDjl4ecLxVQe11Udp9Dgt9Gi0elTVMW4KrgietI0Q5pUKe9kKFaWL444oNkfyZY++4XBeMmi0Ndp9Szpx0cxB8F9B9UStR1f3Jgk9WU650yJ1WhJFZ5yeXbCeOkdp9nA8gosdIevqDi+yVXL5IsJJqQ0DMYocT7UshKWWykmhSamiotQbpF/Ju9kPiC7KBk1R6BOeSJuK2kaEU2yNGHgjNPtOUkZByy6XNZX4iU46b1NQTC7LuhBqMq8+mZxGXci7miOdjFUFhG2fECFkeoYuOaVB4lTnZd4iWzQ5yuuobZ1mR+fQxy0tIPRV2YMbdFHlY3/emvUfCs+rTRb3TVFp8/zCYv/SoNbQRKVO6nys1m+50pWZyyXZBZWejEuw5U7PUAivGyqZgka+ZmLoWmAT29TwbDmranR6Un1/46K22ZWP9chei23aYwvdMbYv0SWVU5n7zeKHvxy+/9vhqwcOkQWH7x45fPnrgJ2sgWlZPIlnWF6Ns7y2ycPFdZ6trLEUW3tDOAxEuvyebBL6bMSHX8DM59LCx1KfSH0q9ZHkPWoz8iwWVw+IxlaIriR4Gk9zICHYlQBMzFB8w2FR5edlg4c7No9SFr+sWPyxbbGQtPgpZolddDTDYveoxPHpK46zOS4qJqYYtaO708b2KLfqFOtdTGM8t3JdwxI8sDSJlixEZuhv/UoZSK49SYor/jTp9g05N6eN7clS6hyJaasNXWKmc3KtB7je0gN1vm18k/up0WWtXVHlY0sUdiWG/wJzVI+1RXgt4gAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do align type (outside of a container), components and tiles to the columns.",
              "title": "Do align type (outside of a container), components and tiles to the columns.",
              "src": "/static/ec64f07a0e7e131d6085ec5e19d07b85/9eefd/32px_do.png",
              "srcSet": ["/static/ec64f07a0e7e131d6085ec5e19d07b85/d6747/32px_do.png 288w", "/static/ec64f07a0e7e131d6085ec5e19d07b85/09548/32px_do.png 576w", "/static/ec64f07a0e7e131d6085ec5e19d07b85/9eefd/32px_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not take type off the column structure to achieve alignment." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADc0lEQVQ4y62UW08jZRjHUVqIn8sPYDR+AxMTrww3Cmpi4jdw44VGL0yMunGTlQQWXChtKZTtUuhyPsi2CLTQmbYz7Zw6p87PZ6bZYJF4s07y5P23b97f/N/nMGPIE0URr9bXjbF/AgeDAUEQvFYkwFew/+NJgGEYJj/W19eZmppi5rPPmZ6ZYUbi0+mZER2v0/+hE6Dv+wnwwYOvGRsbIz35FqlUmlQ6zRvj6aGWeHN8gomJySTGU8M1fUeP5PD65prMco75uXWKmzs8L2+zkKtQLFUobVaYWy6TWy2SXd3g98Ui+UKRlXwx0atrRTK54i0wZkbRANcJWc3u8mQ5TzZf4NF8gaWVNZ4uZ8mWTrC9CMMOqCkhdj+kZ4eci3bckK4Vjjr0fA+1qVHI7VEobVPe3mEx/4JSeVfcvqCwVUVtG9yoXQ5qXdFdGspQt0TXRY8AgzDguq6wWXqZbLr+gItWJG8OMCyPPxt91I5Bp+fw8sYXp16yV1MCcRugm8Eo0BeHXc1iq1yjvHvMwdEpK8+OuWn1cAOoykFNYLrpsnnY4Kx6ISmAy47cThpF/r4DDHzarS6H+w0ur1WaTYXKiSJX7CWg0yuLljiMgftyzRulgyWQi3aEF0hu+9E9QEXjeaXFdk0O/eWwf26hSN6Udo/KmUFT3OqGy5U2dJUAOzEQAd7nUNXYTIB9AdpsHFtyZZO2ZrJTNRN4DIxd9b0BpriKteuLQye6H5gvtckcuKwdOazs2Vw2TZptk2cJfHjlui5FlGNSC+pdmTbRjs/dtvHpdTROjlWqTYdGy+JaNfAcg6BvUG0YaF2Jnsvxpbht6bIvOT+XtukMW2iksYl8to66fPKdzpePPb567PLxjy7TD12+eOTy0Q8e64cWtuMwn60kzf40s8qvsyv8sZRhbjFzCwyTj43HTxmD1HsOkx9C6gO5wjsS70q8L/G2zPtCnyhwmF3eYmFxiYWlHE+y22zJEGzIuN46TD5hPic1ne9nO/xS7PPbhsO3Sw4/rzk8LDp8syhVr5nS5A4bu5fsHZywd3jGad3GlkbVTP/fRdHbGldVBds2JW89rpoGjjXMY12RgkgOlY4pk+JhSUW6li/9aaP3pL069p3RE6Cq6Bzsq9SlIPGY7Z+bNNRhlWN3cdvETR6Pmyll1cVVrB1xqMsY/g3dvY/FSvdPDwAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not take type off of the column structure to achieve alignment.",
              "title": "Do not take type off of the column structure to achieve alignment.",
              "src": "/static/07e0a26e9df5342ed9a2714e790aa58a/9eefd/32px_dont.png",
              "srcSet": ["/static/07e0a26e9df5342ed9a2714e790aa58a/d6747/32px_dont.png 288w", "/static/07e0a26e9df5342ed9a2714e790aa58a/09548/32px_dont.png 576w", "/static/07e0a26e9df5342ed9a2714e790aa58a/9eefd/32px_dont.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h3 {...{
      "id": "the-narrow-grid"
    }}>{`The Narrow grid`}</h3>
    <p>{`This is the gutter scenario that requires the most explanation. Although it’s caused a lot of confusion, the intent is simply to enable more typographic alignment. With the Narrow grid, the container, not the type, hangs 16px into the gutter; allowing headings and copy outside of containers to align with the copy within containers and components. This arrangement can easily be mirrored to accomodate languages that read right to left, like Arabic or Hebrew.`}</p>
    <p>{`Rows that are adjacent will have 16px of margin between them to match gutter. Use vertical helpers here.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Code for the Narrow grid mode is currently using an outdated naming convention. We’ve disabled the attached code snippet until the class name can be updated to avoid confusion.`}</p>
    </InlineNotification>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo grid-demo--narrow">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col bx-demo--no-gutter--left"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col bx-demo--no-gutter--left"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col bx-demo--no-gutter--left"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col bx-demo--no-gutter--left"><div className="grid-demo-content"></div></div>
        </div>
      </div>
    </div>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "usage-1"
    }}>{`Usage`}</h4>
    <p>{`Use the Narrow grid mode when you are dealing with separate pieces of information with separate destinations, but would like to save more real estate. This grid mode will give your compositions a sleeker look and maximize type alignment, within and without containers.`}</p>
    <p>{`As mentioned above, fixed components with labels, such as input fields and dropdowns, should not hang into the gutter. See an example of how to combine a Wide and a Narrow grid scenario in the `}<a parentName="p" {...{
        "href": "#mix-and-match"
      }}>{`Mix and match`}</a>{` section.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACp0lEQVQ4y61TO2gUURTdQrQRRbBQbCystBELixQi2ImdCqJYaRO0FEkys4m72U9iFEIUEkLUShTBVtEQJSaiBIQoKRJJsmvI/mZ2/jPvzf94Z7LBWNj54Mybz33nnnPvnQz+88pEUYTx8Qn09fZBFLMQBAGiICIrdpDN0rPwF5KY/ICA4cE+goByvhcP7xehquoW4eTTKeTKBRSGSyg/GEK+XEJ/oZBioFSCmM9DyOUgEnoGcsjm7+Hq7UF0XSnizLUCTl8exM27ozANDZlEZqVp4fNSHd8rOn5ULTStED69TxB09m04hJgwuwEMfwBG54DiDPDuZ8dycunu/4jdxyex7+QUMkfG0DMyD4Op2FANyNyHwjk2FQXVVguSZUJhHJqjQTNr4J5CDBwssBEE4RbhreIC9px6hQNdr5E59hzC2CJ47KAqq2g4AXQKbDMGyTSxrmlo0H3DdLBck7EmGWjxCJrrIQy3CUcWsf/cWxw+/x57z75B8dkyOGXcUE3IbkiEARTfh071rpC6KhHKrk/qQyheBNmLITMPUdQhvDOxjKPX5nHixhccujSHoZerMD0VFdkgNSEMUtiyLdQMnQ7yNIFOCRhVU+IBKroPyXERbxN2j69i18UFHLz+DZkLX9H/4hc8spzUsJUQkpWmaaGuG9A8DyYpNYhU4i7W2w7WFIa2w5BMTEo4s6Ti8XQdT2YlPJpu4dOKBitwqRluejAhaZEChWxapCJRp3BGyVgao1L9FMb+WLY8nUZFh0lKksUiD+vU0YZhEImXdrnWVrBSk1AzWZqgaTNs6jYpc9ISaK7baQoNlelSB6mbkkP2vBC2H9A7DocCEzAKNKh2dSLdlGRotg3Lp87bpJrGyErqSXGp5TiOd/yJcQfJFm/hX2vHp50cvwFk05OVbYnKXwAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "16px gutter",
        "title": "16px gutter",
        "src": "/static/4d4289938108ca292a1582aa76eae8eb/fb070/16px_gutter.png",
        "srcSet": ["/static/4d4289938108ca292a1582aa76eae8eb/d6747/16px_gutter.png 288w", "/static/4d4289938108ca292a1582aa76eae8eb/09548/16px_gutter.png 576w", "/static/4d4289938108ca292a1582aa76eae8eb/fb070/16px_gutter.png 1152w", "/static/4d4289938108ca292a1582aa76eae8eb/fb104/16px_gutter.png 1728w", "/static/4d4289938108ca292a1582aa76eae8eb/8fefe/16px_gutter.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do align components within containers flush to the columns." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAC3ElEQVQ4y61TaU/iUBQlGcTf6zf1Z/gbJplPk0lEwWWUAVlEFlGWCAVaoJQCZS2UnTPnNTrKGI0Tp+TkXl5fzzv3nncd4LNer/EUPwvHS8LVaoXFYvEp2IRPZP/jsQmXy6X9JxgMYmdnB/v7+9jb27Oxu7v7T7lNOJ/PbcKDgwM4HA64trfh3NrCFvHF6fyTi+hyuWw4ReS+v/ONHjY0DR7/T5zGogjHE4gmEji7uUGE8Toeh/vqCr9YhYA7EIA/FIKP+RHXA8wvmTteuiseWa/B/ctnb/QTXn7oI8LRKNTJBDPumXJvfTaDRRNGrE6dTjFhbjLfULjkYq4i4TgYQCASQZDw8mR/OGwriJdKKFWrNh5aLRj9Ppq9Hgqdjp3rzJ8Vit9yhYycx9cjNw5PTnBMfPN44Dk/RyqTgS+VQoxI3N0h12yia5poDQYokqjDKPJNhfMFFENDtqlD46mNdhtp9lXlxwPLQo0lixJNlliiIkFiDIcoE/3RCG3GFwp5FxdLSJoC3z1V3N+jVKlA5qbuyETTMJAoFpFn2XK9bqsSCussPcl1qVyGouuvFcZlDd50FaFsHQmpjVBlgKxqUq2BjKqi8qi4wANa3S40VpHlAWqjAZX5K0LN7EGxJhguVhgTVWuK4WwBi++Em6LkER2usQUW8wHXRBWD8RgGFW/OMktumB3c1WuQtTokuYwES5EUBWU6a7vJvrWJAtVNOGE610K5HB4kCZlC4bVCfdSFPBqiRwMGYwtlNl80vsfTFaJPJQIin3L+GyQMv0s47iFWkpBMp5HmxiIJx1QyZImyIGQUhMIUcZnfJFw/EjatHk6vI/hxeIgjrxffeRfPLi7s0buIxRDhKN7yTkos2XqP8FlhF9f5B0STt0jyAl9yjpO8QjpdFdPRYNRJ9iHCJ1PKJl2jk8LNKksUTooe2peZUeDjCmlKqlZFXlbs+RQftgQRjRF9Mx6n4y3C31FAmJ4RRn2rAAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do align components within containers flush to the columns.",
              "title": "Do align components within containers flush to the columns.",
              "src": "/static/f943a7198cff7cedd36e6e64dd7afa7d/9eefd/16px_do.png",
              "srcSet": ["/static/f943a7198cff7cedd36e6e64dd7afa7d/d6747/16px_do.png 288w", "/static/f943a7198cff7cedd36e6e64dd7afa7d/09548/16px_do.png 576w", "/static/f943a7198cff7cedd36e6e64dd7afa7d/9eefd/16px_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not hang type into the gutter under any circumstances." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAC0UlEQVQ4y61T2U4iURQkGcTv9U39C+cffJxJdETRiLKDsqhsYQBp9r3pbpZuVmvqXjQZJeM4cTqpnOpwqXtO1Wkb+Dw9PeGlfha23wVXqxUWi8WnIAVfxP7HIwWXy6V8CQQC2NnZwf7+Pvb29iR2d3f/iUvB+XwuBb8eHMBms8GxvQ371ha2iC92+5o7HLATjmdIznNv+drD1drDRrUGn9uDy3gcoVgMN6wXt7eIsAYjERx7vfBwCoEffj+8wSCuyU/IfeRX5K9Dmc0RD0Tg9Hlx7Q/AywNnPOz2eFBpNtHjGYteW7SoPp3CZAhjTie4RT4ifyU4HU8Q9Ydxytu8oRAC4TBcrFc+H+4zGdxXqyiUy6i22ygMBuhpGjpEXlXR13W0yV8JziYmPK5LHDqdOHa5cHp+jsOTE4Q4djKbhTuRwO3dHX4qCkqGAZXoEkUKDZ75hmCrXEeu30e910ej00Gy0UCXt084TtWy5IjaZLIWGQ6hjscoseqsG4JzCmYTSXhTD7jPZtiBDmVooMeDCseNFQoosDsx5iMv6RHFSgXxYlH+viGoaRZuogousg2Esk2kq0MEyhTr6Gi2W0jV6qi2WmjTswKn6LBW+J7hFA36uuHhdGzC6BmozxcwZkuMZwtUzKlMz1ouZJpi5BFrzTRlykNyhSMbtKE/Gr0Z2bRgdFVk2E2JtxaVEuKlkky2xJFkmhxLIM+UxaqIboMMLEc70vn8pofDnoYazdfYgUGU6FOfHWi8vUzo7ERA8Cl3skXB0LuCfR2JsoJ4MokUDxZFwlzkIcUVIciqP6csOnxXUKzNSNXhjkXx/egIzrMzfOM+XrjdiPBTdEejCHMn79Jpudjm3wRfOow+FuX3m3h4wJWo7LbNVHPdLlqsbYp9UFCEMkDF4lgi0dkMVeElufBQ7J7KKvDxDhlKqtVEjgssvk/xR/GlqAxG+CZTJv+T4C/v2piDBaQTFwAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not hang type into the gutter under any circumstances.",
              "title": "Do not hang type into the gutter under any circumstances.",
              "src": "/static/281e958a050de5c0b764e3ed902b9629/9eefd/16px_dont.png",
              "srcSet": ["/static/281e958a050de5c0b764e3ed902b9629/d6747/16px_dont.png 288w", "/static/281e958a050de5c0b764e3ed902b9629/09548/16px_dont.png 576w", "/static/281e958a050de5c0b764e3ed902b9629/9eefd/16px_dont.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h3 {...{
      "id": "the-condensed-grid"
    }}>{`The Condensed grid`}</h3>
    <p>{`The Condensed grid collapses the gutter to 2px. To achieve this, containers and components bleed 16px into the gutter on either side of the column. This grid mode is most often used in conjunction with another grid mode and arguably requires the most design skill to incorporate into layouts.`}</p>
    <p>{`Rows that are adjacent will have a 2px of margin between them to match gutter. Use vertical helpers here.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo grid-demo--condensed">
      <div className="bx-demo--grid bx-demo--grid--condensed">
        <div className="bx-demo--row">
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
          <div className="bx-demo--col "><div className="grid-demo-content"></div></div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid bx--grid--condensed">
  <div class="bx--row">
    <div class="bx--col"></div>
    <div class="bx--col"></div>
    <div class="bx--col"></div>
    <div class="bx--col"></div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "usage-2"
    }}>{`Usage`}</h4>
    <p>{`When you have separate pieces of information that form a larger picture, like a dashboard or an overview page, that’s a good time to use the Condensed grid.
You’ll also see this grid mode in portals, resource tiles and other introductory UI. See it in action on the `}<a parentName="p" {...{
        "href": "https://www.ibm.com/design/language/"
      }}>{`IBM Design Language homepage`}</a>{`.`}</p>
    <p>{`As mentioned above, fixed components with labels, such a input fields and dropdowns, should not hang into the gutter.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACDElEQVQ4y61R247TMBTsV5dtmxaEggRvPIHUBz4KrRa03e3m0qS5OI5zT5rYw7HbLiD1CRFpcuxz4snMeIb//MyklNhut7BWS2w2a1iWdQMrrGh+C3q2WC5g2zYYY5gpJfHl6zfM5zaWi/e4m7/F4s07gzsN2uv+xvqA9epP2FjrHsFa2vj08TOyLMNMy2wbIIlO4OmInEkUXKHMAZEp6k3oakCezpiGv9dqvPTGi2X9SuMKB1cg9EvEYYPk2CIKGrOOw/N6t2NwnBK+WyLwBKGA73BzxncEorDCNMkzYZ41CN0Medqizkc0YkJfwaAjtKUCi2tSPKAg6H7BezD6XrCRQO7YgEnKq0KBxwcXux8eAiclcobDpR49hirXh1rwpIb7HKHJT0Q24emlo6h6ctQjpiqvhJzV8PaJIfHogLuP4D+GeN6lcMmOVlwRiWAd/YCTi3PewaEh5UQW9oii7kpIF1CMdACvNnu6hEHbLYGmUGgJXXWGng80Z1GL/VNqMtQZs6T/bVlQhjxuUIvRoCsl1ckoSwKBMuuJeDKEutdRpnk6wHlm5CI2l6IJXy0LPiCjDEoKXBO3hUSedGSNCMMaFZ/MTCut+GjmPBnwcO/j/ruD3c+YsuyuCslSK9GSjVOncOovtSNrrUKtrTYKY68ufb0G6vKEg0cZvxxxDDgq2huFSmmN6h9w+/kFMBO45fWIJr0AAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "2px gutter",
        "title": "2px gutter",
        "src": "/static/6e68153f104d18e13d20d5f713b3513a/fb070/2px_gutter.png",
        "srcSet": ["/static/6e68153f104d18e13d20d5f713b3513a/d6747/2px_gutter.png 288w", "/static/6e68153f104d18e13d20d5f713b3513a/09548/2px_gutter.png 576w", "/static/6e68153f104d18e13d20d5f713b3513a/fb070/2px_gutter.png 1152w", "/static/6e68153f104d18e13d20d5f713b3513a/fb104/2px_gutter.png 1728w", "/static/6e68153f104d18e13d20d5f713b3513a/8fefe/2px_gutter.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do keep all type (inside and outside containers) aligned with the column grid." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAC9klEQVQ4y61U2VIaQRSlSjH5Xd/UfEX8kLwkqYoRBVmGRdlBVIwLYRtmY2aYYfaT2z2oEZKHlJmqLs50H07fPuf2JEBPFEV4+n3rSPwuGIYhfN9/0+CCT2L/4+GCQRDwl3w+j93dXRwcfMD+/j4fe3v7/4S5oOd5XPDw8CMSiQR2dt5jezuJZDKJra3kM97efkdr8fgbfuXhaDxCNpNHPlVHpVjDeaWG7HENlRLhchXHXwTkMgJSpxkcfS4gdyYgmy7E81kBZ2nhRfBJdCYq+PopjdRRBqepDOEMjo/SfKPJnQXXDqHoBmYDB8uFB8vwIP/0sLQY9l9X6HkupmMJNaGPy/YNri77z/iyc4XHaw2abGIiKZAG7kokFrQtF3PZei0YBD5UeQ4hU6cj1lG9qHNcPW9AnmnQpxFCsvv28QEn3wooCmUI+TJOj4qoVYkj6n8QVHSUcy3ULlpo1FsoZ1uoV1u46vWhjHwu+DAaQjhroNnooFFrE7+NdquLXre/eWTPCeHN2WTcV0t1hWkYU8BfRtCtJRxt1Xg076gr6GNN0I/NlYcOFjoTD6CNAzhWAN+NMJ+Ez6GIA5tz2RrjMK5jha8FfRJcGDYermfkGZVJl2chxTuHNCRKdq6YmGkafvSmMOYL+hNgyeBc116r0CcPNVVHs0LJdijl3g0a5ThtXV1Aowr9JXA/HKJEPrebXTTrXVTIw+urPkzNWQ8lgCypSH8vIZ8tQiiUOC7kiui2e1ApFFbt3WCAzHGZN3u5eIHcyTl1RI3a63ozFMf2YYiE7fiYDPsOeFjzSQSPQtFMC7oYrwcuYM5i7FjRpqC7DLhv7GiBF/He8xyGQRjPgmwj1kLsnXEYd9ND+p6ZFEq/M8RkJL2IOHEF8k+XeymqCnrNR24Pq55xGNdbF2QV6toC4zsTmrSAqduY3tswNHuFLY7Z1Rve6pyrqzbEB5sXoiv2pqAq6xjdmlBmBjTFwLBvQJVeMLvLI3FGWOctxHh8ntYV0cAvUVGou9pCyNUAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do keep all type (inside and outside containers) aligned with the column grid.",
              "title": "Do keep all type (inside and outside containers) aligned with the column grid.",
              "src": "/static/41a0250160fbb7c909135fae1c69e800/9eefd/2px_do.png",
              "srcSet": ["/static/41a0250160fbb7c909135fae1c69e800/d6747/2px_do.png 288w", "/static/41a0250160fbb7c909135fae1c69e800/09548/2px_do.png 576w", "/static/41a0250160fbb7c909135fae1c69e800/9eefd/2px_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not hang type into the gutter under any circumstances." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAC+klEQVQ4y61Tx1IbQRRUFRL273JD+CvMh/hkDliAxCoLBRQJAoOxsjbn3H4zazACfHDhrZpSa9+b3n7dMynQE8cxHn/fu1LPCaMoQhAE71qc8JHsfzycMAxD/qdUKmFnZwd7e5+QzWb52t3N/hPmhL7vc8L9/c9IpVLY3v6IdDqDTCaDra3ME06nP1AtWX/DGx5OZ1MIhRJKRx00qm2cNdoQcm00aoTrLeQOqhCOizg9PCVcQfG0DCFfQe4rYaGCU8Kp5+myZ7WUcPAlj6PDAk6OCoQLyB3m+YcW9zZczYO5NCBOAtimB0v3If704do+bD3YVOj7HhazNdqVMUb9a1yOxk94NLjEjysF8kKFwQlDOKb/RGjqDuSVsUkYhgFkUUWl0KERO2g1Oxy3zjoQVwqMNWBKFs6EGvK5GqrlOiqlOvLfapg8TOEY4RuEkoZ6sYd2s4fzTg91oYdOq8fVKvMQluKg3+iiXuqj2+njvN1Hg/BocAF5bb4e2Xcj+Cp7mZwrR/6D9RW4h7AAT/t98KjmygkMXLwgDHw4FnkycWFqjDyEMgvJ8JB/SJlHcFSXPNSpx4NnB3CtpCfwIrhmtEkYBMxcG/dXK/KMZNLlMcm3OKCaF2Nyq8OUbSzv5ni4kREFbBMJFqmH7oahuC8IyUNF1tBtULIDSvniGuf1JG1VNqAtaZNoYdgk38pD9LtD8nFIHg4grmVKPXoZSsgLLLWSUEWlXOO4XKxi2L/gI7NQ2uUzCCdNOvBN1KtNFI+b6HUHb4fiki86KfFt8JEYZmYzD7UFhUTeeorHjxCrh5SRQWGFfgzPil8Tek7IfQucpElbxPCdmDD4yIzQVxNC5iursR5WZyJehBLAoFDGgwnm03VCskgUss3TW4OPPBk/YDya8evGaoyQqTVU97VCTTEx+25AIT8Mzcbizoau2E9YXemQJxJmdyZ01YLGUqc7zoSodIteEcqihumNAYk2KpJOaohgnWD+fq5A+imSWhOKqPM+1sPqEp3PX1REqLKHykC0AAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not hang type into the gutter under any circumstances.",
              "title": "Do not hang type into the gutter under any circumstances.",
              "src": "/static/90730d1024878150de7dc09d5ea1e778/9eefd/2px_dont.png",
              "srcSet": ["/static/90730d1024878150de7dc09d5ea1e778/d6747/2px_dont.png 288w", "/static/90730d1024878150de7dc09d5ea1e778/09548/2px_dont.png 576w", "/static/90730d1024878150de7dc09d5ea1e778/9eefd/2px_dont.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h3 {...{
      "id": "mix-and-match"
    }}>{`Mix and match`}</h3>
    <p>{`You can use rows to mix and match grid modes. In this situation, margin bottom between the rows can vary. Because of scenarios like input fields where the label text doesn’t align to the input text — both the Narrow grid mode and the Condensed grid are most often used in conjunction with the Wide grid mode.`}</p>
    <h4 {...{
      "id": "usage-3"
    }}>{`Usage`}</h4>
    <p>{`Unless you’re using the Wide grid mode exclusively, mixing and matching is going to be the rule rather than the exception on most platforms — especially since our labeled components (input fields, dropdowns etc.) require a 32px gutter. We’ve provided a couple of examples below and will keep adding helpful uses cases as we find them.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAACZklEQVQ4y62T204aURSGeZnGtBc1EeUwDAMEDVGQg2ArVpsqopxBzoOoNU3TNvVU1Cdoe9OmVwVEe7j0VQoF77iq4t+19yDxAZzkyzrMmn/+vWe2Cvd8qW5ueri4uMD3ag21Wh3VmhIH1Pvcrft59RZ69vz8B7rdLlRXV1fY3dtHNldELi8jmUojGksgGk8RScR4JGJJXg/6iRTiyTQSbJ56uYKMVqsF1TUJ7h98QDZfxMbmNgryBgmTeKHEh5RY4veVWka+WMJ6Joe1cBQrq2Eu/nLnFZrNpuJw/7CC9XQOmWwBaRrksJzI5BTS2fwAJhgIhiBZbBDN47BYbVh8voR2u80E/+Ho+ARb2zsok8PSxibkUplTKPYdMZd5eeCQESBnRqsDBmkcgmTFU/8iOp0OLfn6Gq/fvMdyMIJwbB2haArBcAIroTgCoRRWQ1EE1yIIkgCLzBnblqXlIExGA0yiBka9Gn6/nwQv2VcGookM1BoBetEMjd7I0QoUjZPcgY7lehFa3pcgSmZM2GcxNneAYe8uhpx78Lwo4rL9B6per0dLKMPh9GDK4YbeIPUxkZgVducMJNonJsb77KU6gQR9EOb3MOJ7hyHHW/gCsiJIBhGJKw4NRgsNixgjmCONYIHOYOZiY1qB+gbeH6VZ26QdtgkzLCYRom4ECwvPlCX3aA/z8hbsrifwzM7D7Z2Da2aOoh8u7209C6fbC5fHx3FTPTXtxqPHo4QaDx4O85W023+Vj8JOys9fv3FGf3v9tME5bZwN8vrp3byBxtk5vnz9hsPKMSpHJzig3+7jp8/KSbnvs/wfNs61Vbm/EV4AAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Mixed use",
        "title": "Mixed use",
        "src": "/static/656d06fbae3974006a290ab6e9e5fae5/fb070/Mixed_use_revised.png",
        "srcSet": ["/static/656d06fbae3974006a290ab6e9e5fae5/d6747/Mixed_use_revised.png 288w", "/static/656d06fbae3974006a290ab6e9e5fae5/09548/Mixed_use_revised.png 576w", "/static/656d06fbae3974006a290ab6e9e5fae5/fb070/Mixed_use_revised.png 1152w", "/static/656d06fbae3974006a290ab6e9e5fae5/fb104/Mixed_use_revised.png 1728w", "/static/656d06fbae3974006a290ab6e9e5fae5/8fefe/Mixed_use_revised.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">
  1. Condensed grid mode <br /> 2. Wide grid mode{' '}
    </Caption>
    <br /> 
    <br />
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do apply different grid modes to different components (like input fields with labels and tiles)." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADFklEQVQ4y62T6U5aURSF+WH0dxsNM1xQ4DKIIHARLXCZBRlkUOuQDhbEqYmibaw18RlsKw59HZ/KaXWfYzVSYprUnuTkbs7wnbXX3ihA4/b2Fvff507FY+DNzQ2urq6eNTnwHvY/BgdeX1/zHxcXF2i1Wjg4OMD+/v4/TQ68vLzkwKOjIygUCvT19aGnp6dr9vb2/nVyIMudjbPTMwzoVJBCE3A4XRBFO3yBIOwOB2yiCJfbA0EwQTCZ4Bz2wCgIMJnMFI/AaKTYPNgJ/PHtOwZdVhwcHmJ7u4WtrW3s7X/Fzs4uNjc34ZNC0Or00OkNcBBQo9HCYBDgdHuh1epgIGgH8KTdxkvNAIITYdjtTnrRjBGvn6tgCpgqHQHZRa9P4oqMRtMdXKuF3mDsBLaPj6EUNMgWighR2uyiVXTxg0zZ8Mgo/9rtDkzmCpCkIAeKDjf0pNpIdvwBbGPAoMbb5TqazVV8qNextvERciyOSCSKcnUWIsGGLFa8W26g3mig0VjBSnMd4UiEn+tMmYD9eiVSuSkCyPxQNl9CICDB5/Mjkcpy2OCQhSuMRmUOyU4V+b4UHOsGvtD0k4evYLPZoVZrYBGdUJP5SpWaUhsmr3Q8bRYrlSq+x2xR0T5b7yqKwWZCqVpDPJ7k6fmpsuxrs4kUj8NitfFiuNyj3FtBMMMzGuCFGxyydvdhKB7GFrXJykqTfGyiNjtP8AQilN7Sm/dYXFxEsVjCzu5nrK6tkX+rmK7MICrLiCeSncDzs3OMTkiYXVhCLpdHOpNBoVRFjIBROYZiuUZrk0ilM6jUXiND8WQ2R2cqkGm/C/iTgJ6QH8VKjUOYqnyxwmM5FqO4zONEMoVcfvqhKGz9SaB3PIDp2iySyTRi7HDpN1COcTi7lEylH5QzeP4phXcp3wETiRR/vVSe4Skm6SLzisVsPZMtYIK6IUz9ydSyPu3uw/YJ/OExfNr7gvX1Dd7cldocf51N5hUDFOif1GJFWV3jxSuRtwzIFHdW+eQUw5IHtfkFZCazHakxD1nMwMyKcnUOKdpnRXpclF9oBbtf0dS4BwAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do apply different grid modes to different components (like input fields with labels and tiles).",
              "title": "Do apply different grid modes to different components (like input fields with labels and tiles).",
              "src": "/static/205bf1f3bb53f9157a97046e1ba53a0c/9eefd/Mixed_use_do.png",
              "srcSet": ["/static/205bf1f3bb53f9157a97046e1ba53a0c/d6747/Mixed_use_do.png 288w", "/static/205bf1f3bb53f9157a97046e1ba53a0c/09548/Mixed_use_do.png 576w", "/static/205bf1f3bb53f9157a97046e1ba53a0c/9eefd/Mixed_use_do.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not apply different grid modes to the same component on the same page." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADAklEQVQ4y61T6U4aURjlh9EH6A+VRRgEBEVARIKsA4wLMgyraBeX1FYElyi2NrH2IWxFxefxqdxOv/kQI2rTpHaSL/fM3O+ee+6cczWg5+7uDu3xtaV5THh7e4vr6+tXFRO2yf7Hw4Q3Nzf8cnl5iXq9jqOjIxweHv5TMeHV1RUTHh8fQ6PRoKenB11dXc+qu7v7r8WE6tnV57zZhLFXi8nJMEaco3A4huGbCGB4eAR2uwOj7jGYBIHLOeqBySTAbB7ECGGjycS4g/DnyS+4LXYcff+B7Z0dbG1t4cvBN+zu7qFarcI9NgGtVge9YQD2ERd6+/rpXc+4v1/L3zsIG6cNmPv0SM8pCEdjCAZDmJqZI5V+eMd9kKZTsFhtpEZAJJaAxzPG30MRkU+gnqqD8KTRgE0voFbbxkathkplA+sbm0gkJSQSSeztH8BNJFbbEM1v83y1WsPapwpEMQ5pavop4QmGDAKKhQXEiSAWEyErecZRwvniAquwEaGSK/K8Opemnpgo0qbSS4RmlEqLSEhTEOMJZLIFVhgjBYXSApnhgm3IjmxhHnGaT1JfJlt8wC8eOZ+fZ7K2QhVH6Z/mimVWaKX/mM7kEY5EWXkqnSUcof74U1NOYXjTh1AoBgdFRY2B2ztBo4Uj4hn3Y8BoIjcNcLq87KrRKFBsxqDT6XnuGaFVZ0RGzvHugmDmhSYajdTsdHt5kVoONSraVlTUHgONal8H4VnzHFIwiv36V3xer+Dj2hr9t8UHU96+X8b8fBmzqTnUNnewsrqK5eUVPn4oFOaezpty0YToD2LpwyqUbA6ynCE3Sw+Oq6bIGQUzs7NYfLeEjKJAUXLsuPiSKc2LC4S9fuSyJcSoIUJGyEqBCcV4nJXwQnJdNUKdV92X27FJSs8JI+MBymGZd4u3Y3NPKBNOShIHmKNC31uxKfxJYZMVFvJl3k3k3e8VPsa0uKUwygpTMuFIC3cQnp6d8V0OBMKwUngHBy3w+gJ8fx+wxcpuujw+dl6gaLk84+S8kfFvOX66sXOtU6EAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not apply different grid modes to the same component on the same page.",
              "title": "Do not apply different grid modes to the same component on the same page.",
              "src": "/static/c65c22fc0f6857068a81adf77aec11d7/9eefd/Mixed_use_dont.png",
              "srcSet": ["/static/c65c22fc0f6857068a81adf77aec11d7/d6747/Mixed_use_dont.png 288w", "/static/c65c22fc0f6857068a81adf77aec11d7/09548/Mixed_use_dont.png 576w", "/static/c65c22fc0f6857068a81adf77aec11d7/9eefd/Mixed_use_dont.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAC7UlEQVQ4y42TSU9TURiG+UeSAJ2oFElcmXS67e0trVpMdCfGqBEEkcSNMSoGjQtdOE/MnShVRJAiQRwQCUUiGIeogFDEKjiQx3NPjSx04eLNme59zvsNJ09VVTRNQ/P78f+nNM2Pz+f7Szorz+v1oigKbrcLp9OZk8OB4x/S9/Vzj+IQAFWCNb/25yIJ1A/27Kuiqqae/dW1VNccpPZgPXX1hzlQe+iPqmvqqDpQx87d+wlsq8ZWugmDoZCiIgMmkwmr1YrL5SLPr/loCSfoHRylp/8hfQMP6E0NEU/20D84LNZD3BN7PX33OXLsDOevdHKhKYXPX4HZbBSg9RJYUlKSA2oCeLOlg4GhEe72D3Hrzj2inbe5fK2ZtnCclvZoTm1RmtsiXL/RQSyRJFQRori4mLKyMgm0WCw5oKp6udHUyuDwCMOPx3j8NM2T0QkejozzSJ8/e85YeprWjk6aWyNE4l2Eo10Eg5tFuIUSajab14AeUZBkdx+LX1aZyyyTyf6Qmln4wvynbyx8/sHSMoyOT0mn7ZEE4VgXoVCFzNsG4dBmKxWy5YCK4qa7N8XKKgL6k09fV+W4KEZ9rsOzKzDx4i2t7TEJjMSSBAJBCgoKsBRbMYqQdZe/HbpF3vrkzzPzWeHyq3D1nfml73xc+sbs4goL2VXGnr9aA8aT+MvLyc/PlyCj0YTJrIfs1vvQIz7qZPLlO56lpxiffMX06xmm38zyYS5DJrNA9vMS6YlJUZSoLFRHNCEcBkQOi7CISlsM+disRtwup3DoUei+mxKh/WRucVnmbVbkMitykH4Pl1JwcQCu9XwgLqobS3STEC21fccONpSWYNvoYF3ZNgo2VuBUfMKhx0PjqTPy5qaWsGwNXdF4grNXE+w+1kXl0dtUHW3iRMNJjjc0cuJkI5WVuwhtCaBs3Uth8ByG8tO41C1626gymblntSa73Y7ishNQ7QR9djSvQ+ZIf6K6PMKI/mxVryLOXFKqWP8Cqq+hpeRbcpIAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Mixed use 2",
        "title": "Mixed use 2",
        "src": "/static/aa16dd9f6b77c64c676e05ff84474550/fb070/Mixed_use_2.png",
        "srcSet": ["/static/aa16dd9f6b77c64c676e05ff84474550/d6747/Mixed_use_2.png 288w", "/static/aa16dd9f6b77c64c676e05ff84474550/09548/Mixed_use_2.png 576w", "/static/aa16dd9f6b77c64c676e05ff84474550/fb070/Mixed_use_2.png 1152w", "/static/aa16dd9f6b77c64c676e05ff84474550/fb104/Mixed_use_2.png 1728w", "/static/aa16dd9f6b77c64c676e05ff84474550/8fefe/Mixed_use_2.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">
  1. Condensed grid mode <br /> 2. Wide grid mode{' '}
    </Caption>
    <br /> 
    <br />
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do apply different grid modes to different components (like input fields with labels and tiles)." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADR0lEQVQ4y62Tv28adxjGUWtS71n6D3TJf5BKVadKHSJFXTp1zxCpkoeosTw0kbp0aCXHQ5dOXTp0iFRFsavYxsbGGAfOh8Egfvk4OO43x/0Eh7vj6cuX0tbGUobkpFf3guDzfZ7nfb8J0DOZTDB/v2sl/g+M4xhhGL5TMeAc9j4eBoyiiH04PDzEysoK1tbWsLq6eqUeU333+O09A47HYwZ8tr6ORCKB5eVlJJPJK7W0lMSHSx/h1q1Zzfvktf5Khoos4+XuNl6mCsgcc8i95rCZ4nCU45DNFfB88wip/Qx2qZ5vZrCXzmBnb9bvH2Swncr8B5xDG3IXv/+5i7+209hJpfHHizS2Xu3Tn49Q743gj2K4QYiWGlIfwgkiCFqE4WUE24+uKoxoSlVRwNZBEbl8CfkCj619HtkTHoXTEs6aFjTTYVUVPRh9F7Jmo3xhU2+jpznX1oaATVlC9kyE2DPZqdPTB94b2O4IlbYLw/IhShrS+RY6PRWmPUStG6A/8CDr7jWFNBzR0MDVNTTbCmTVAFfTCRLAG05tjtG3AwY9pe87kkpQE3zDhBdcLloOCdjtG8iWOsifVnBWruJVpoyu0mfAmjSEOfBJVYDMqQD+7BwFvoKdbAV9y4HljRczFDQFZcGGTqocb4i6NKLMPOiU27ngQqfcVMNh9i0nIPiIfjMkyy56+g0ZNnpdHHAttMjyDDgkOKmyPDaIqcJOT8chJ0CS9X+Blu1DoYMXLVsmjstd5PkqypU62alCIUU0H7S0kFSRGqqjoogiRcKXatg7qcH1aaXeYHEoHVND6cJEt2dA1QwU6zqzOCB7ta5PAyH7VMVmnylsSwb4usEsaxTTQoYNRcVJzcaFTNZUD1zDh6TRvlE+xdZ0B2c5VtoeWxWt/08U1g0ZBjTJr38Z4O6TEF/8GOPLn2J8+n2MTx7FuLMa4/aDCX7bG2EUuCgJHstVpdwq4gyuGNcy9Al4f8PG5z+EuPfzBF9txPjsKYEexvj42xgffDPBr9sjXA4JeDFTpZLiiugyy4rhLlpuqipyZLkpeWgrHgqN2ck6WTsXZu+55bcqjMYh3RQV1c70bnpwHBe1jstym6qpitOr5zLodA9vAv4NqSiOiQJyNMwAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do apply different grid modes to different components (like input fields with labels and tiles).",
              "title": "Do apply different grid modes to different components (like input fields with labels and tiles).",
              "src": "/static/c11e198b456d3efc1dee974592251fd9/9eefd/Mixed_use_do_2.png",
              "srcSet": ["/static/c11e198b456d3efc1dee974592251fd9/d6747/Mixed_use_do_2.png 288w", "/static/c11e198b456d3efc1dee974592251fd9/09548/Mixed_use_do_2.png 576w", "/static/c11e198b456d3efc1dee974592251fd9/9eefd/Mixed_use_do_2.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not apply different grid modes to the same component on the same page." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADOklEQVQ4y62TXU8jZRiGmyiUMxP/gEcmeqYe+QOMh+4f0QMSjSDRQw80JvI39GDVmIBh2VKWgpSlLZS2tOz2i3a+O52vln5NL5+OsgJlPVknedJnpvNez3vf7z0x5JpOp1z/vmrFbgLDMGQ8Hr9SRcBr2P9xRcDJZBLd7O3tsby8zNraGqurqy+tFakvV+7vI+BoNIqA6+vrxGIxlpaWWFhYmKt4PB7VwmKc116Ps7g439/yUFHaJJJ7bO5kSf2ZIX2UYSORYf8ww8HhMb/8ccDj7V22NxM8lH5nN8X2ToqHGymST1I8SqT+Bb6AaiY//bbD5qMkjxO7/Pz7LptbSVmwz4U6wHcHOLpH1QgJ+mPc3oSaPqE/mOAEk9s7HIv0Zktlay/P4dNTnh7n2EzmOEjnyGTz5KtdNKWDWtcpXfYwOy6K7nBWdaR3aOvuHeB4hKp3SOdbNFpmNHU2vesPcbwrig0fQ+tSO6/zJFOV4RqW06cs8E7XRzG828DRaCh/BJxcWFzUlEh+tmxg2L1I3nNDZElvXFpkL8wI2Gxb5OR9vzeYlzwaDui6fdJnLY6yBU7PSmylzrhUrQhYEQ8tw8Vs26ROGuROCxznimwfFOnYLrY/uuvhEMNyKNY99E4P1+9TaV2hWyLVcik05Llqo9Q0is0A2+mJ5Ct5py/KPNrGPR4qmsV+tsazuvIPsC+SAyzbpyQQUxbVy01SuTqttv4CaDsBqgyekzwz/6igcCRSzooVkVNCNV2G8jHVrKl4KLtRHfZPm5zkS+TyZXbSZbzgimDIfYfiU6jZYraBpptyQIZIdsXbHuWWSDQc9KY8r9q0FIO6pCFXMSPJuhzYvIeSrewzn+ftgKbuc1QJaKieRMIViPjUus5hEA3XO2KFxMmy73gYRswhG8c9Pvgq5OPvpL4P+ejbkPe/Dnl7JeStL6Z8+M2A8rlJRQaaHQ9NfJvlcwZXTX8e+Gu6x3urUx78GPKJ1IMfQt4V2JufhbzxKbzz+YDz0k2gJ0Avkqya9wTblK3nRPJFy6eh+aTPfS5F+mxxoRGgSWy0hhHFZibzpTu8CSw3/wY4rkfl0hNfvWhxqTn79P4b+BeoBY6kAntVfQAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not apply different grid modes to the same component on the same page.",
              "title": "Do not apply different grid modes to the same component on the same page.",
              "src": "/static/4d1dac2a81ea7e5923853a31ea7810df/9eefd/MIxed_use_dont_new.png",
              "srcSet": ["/static/4d1dac2a81ea7e5923853a31ea7810df/d6747/MIxed_use_dont_new.png 288w", "/static/4d1dac2a81ea7e5923853a31ea7810df/09548/MIxed_use_dont_new.png 576w", "/static/4d1dac2a81ea7e5923853a31ea7810df/9eefd/MIxed_use_dont_new.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h2 {...{
      "id": "incorporating-the-shell"
    }}>{`Incorporating the Shell`}</h2>
    <p>{`We cover more about shell behavior on the 2x Grid Basics tab in the `}<a parentName="p" {...{
        "href": "/guidelines/2x-grid/basics#grid-influencers"
      }}>{`Grid influencers`}</a>{` section. However, we’ve had a lot of questions about how the shell works in conjunction with our grid. That’s why we recently incorporated the shell into our grid tooling as a Sketch template. We’d also like to show it in action here in a product.`}</p>
    <br />
    <h4 {...{
      "id": "collapsed-state"
    }}>{`Collapsed state`}</h4>
    <br />
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <aside className="side-nav"></aside>
      <div className="bx-demo--grid">
        <div className="bx-demo--row hide-sm">
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 2</div>
          <div className="bx-demo--col">1 of 2</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 1</div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<aside class="side-nav">
  <!-- Toggle side-nav--expanded class above -->
</aside>
<div class="bx--grid">
  <!-- ... -->
</grid>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "expanded-state"
    }}>{`Expanded state`}</h4>
    <br />
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <aside className="side-nav side-nav--expanded"></aside>
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
          <div className="bx-demo--col hide-sm">1 of 8</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
          <div className="bx-demo--col">1 of 4</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 2</div>
          <div className="bx-demo--col">1 of 2</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col">1 of 1</div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<aside class="side-nav--expanded">
  <!-- Toggle side-nav--expanded class above -->
</aside>
<div class="bx--grid">
  <!-- ... -->
</grid>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "usage-4"
    }}>{`Usage`}</h4>
    <p>{`The most commonly used product shell is the flexible panel. The collapsed flexible panel expands when the user hovers over any portion of it. When flexible panels expand, they either condense both the content and the grid or they push content beyond the edge of the browser.`}</p>
    <Video vimeoId="363369752" mdxType="Video" />
    <h2 {...{
      "id": "alignments"
    }}>{`Alignments`}</h2>
    <h3 {...{
      "id": "offsetting"
    }}>{`Offsetting`}</h3>
    <p>{`Use offset classes to offset content by a given column span. These classes increase the left margin of a column by `}{`*`}{` columns. For example, `}<inlineCode parentName="p">{`bx—offset-lg-8`}</inlineCode>{` moves `}<inlineCode parentName="p">{`bx—col-lg-8`}</inlineCode>{` over eight columns.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-15 bx-demo--col-lg-1 bx-demo--offset-md-7 bx-demo--col-md-1 bx-demo--offset-sm-3 bx-demo--col-sm-1">1</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-14 bx-demo--col-lg-2 bx-demo--offset-md-6 bx-demo--col-md-2 bx-demo--offset-sm-2 bx-demo--col-sm-2">2</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-13 bx-demo--col-lg-3 bx-demo--offset-md-5 bx-demo--col-md-3 bx-demo--offset-sm-1 bx-demo--col-sm-3">3</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-12 bx-demo--col-lg-4 bx-demo--offset-md-4 bx-demo--col-md-4 bx-demo--col-sm-4">4</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-11 bx-demo--col-lg-5 bx-demo--offset-md-3 bx-demo--col-md-5 hide-sm">5</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-10 bx-demo--col-lg-6 bx-demo--offset-md-2 bx-demo--col-md-6 hide-sm">6</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-9 bx-demo--col-lg-7 bx-demo--offset-md-1 bx-demo--col-md-7 hide-sm">7</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-8 bx-demo--col-lg-8 hide-sm">8</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-7 bx-demo--col-lg-9 hide-md">9</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-6 bx-demo--col-lg-10 hide-md">10</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-5 bx-demo--col-lg-11 hide-md">11</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-4 bx-demo--col-lg-12 hide-md">12</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-3 bx-demo--col-lg-13 hide-md">13</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-2 bx-demo--col-lg-14 hide-md">14</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--offset-lg-1 bx-demo--col-lg-15 hide-md">15</div>
        </div>
        <div className="bx-demo--row">
          <div className="bx-demo--col-lg-16 hide-md">16</div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--grid">
  <div class="bx--row">
    <div
      class="bx--offset-lg-15 bx--col-lg-1 bx--offset-md-7 bx--col-md-1 bx--offset-sm-3 bx--col-sm-1"
    >
      1
    </div>
  </div>
  <div class="bx--row">
    <div
      class="bx--offset-lg-14 bx--col-lg-2 bx--offset-md-6 bx--col-md-2 bx--offset-sm-2 bx--col-sm-2"
    >
      2
    </div>
  </div>
  <div class="bx--row">
    <div
      class="bx--offset-lg-13 bx--col-lg-3 bx--offset-md-5 bx--col-md-3 bx--offset-sm-1 bx--col-sm-3"
    >
      3
    </div>
  </div>
  <div class="bx--row">
    <div
      class="bx--offset-lg-12 bx--col-lg-4 bx--offset-md-4 bx--col-md-4 bx--col-sm-4"
    >
      4
    </div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-11 bx--col-lg-5 bx--offset-md-3 bx--col-md-5">
      5
    </div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-10 bx--col-lg-6 bx--offset-md-2 bx--col-md-6">
      6
    </div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-9 bx--col-lg-7 bx--offset-md-1 bx--col-md-7">
      7
    </div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-8 bx--col-lg-8">8</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-7 bx--col-lg-9">9</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-6 bx--col-lg-10">10</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-5 bx--col-lg-11">11</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-4 bx--col-lg-12">12</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-3 bx--col-lg-13">13</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-2 bx--col-lg-14">14</div>
  </div>
  <div class="bx--row">
    <div class="bx--offset-lg-1 bx--col-lg-15">15</div>
  </div>
  <div class="bx--row">
    <div class="bx--col-lg-16">16</div>
  </div>
</div>
`}</code></pre>
      </Column>
    </Row>
    <h3 {...{
      "id": "vertical-spacing"
    }}>{`Vertical spacing`}</h3>
    <p>{`We intend to add a demo on vertical spacing soon, along with more guidance and examples around key heights and spacers.`}</p>
    <h2 {...{
      "id": "techniques"
    }}>{`Techniques`}</h2>
    <h3 {...{
      "id": "full-bleed-content"
    }}>{`Full bleed content`}</h3>
    <p>{`By default, the grid maintains a max width of 1584px, but allows content to span infinitely. When you want background colors or an image to go edge-to-edge, you will need to create a wrapping node around your grid with the color that you are looking to apply. In this example, we create an outer div and give it the class name bleed which we then style with a background color.`}</p>
    <p>{`If you want to remove the default max width, you can do it by using a grid modifier class. This will allow your grid to span infinitely.`}</p>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo--bleed">
      <div className="grid-demo grid-demo--wide">
        <div className="bx-demo--grid">
          <div className="bx-demo--row">
            <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
            <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
            <div className="bx-demo--col"><div className="grid-demo-content"></div></div>
            <div className="bx-demo--col "><div className="grid-demo-content"></div></div>
          </div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bleed">
  <div class="bx--grid">
    <div class="bx--row">
      <div class="bx--col"></div>
      <div class="bx--col"></div>
      <div class="bx--col"></div>
      <div class="bx--col"></div>
    </div>
  </div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "usage-5"
    }}>{`Usage`}</h4>
    <br />
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "64.28571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAYAAACpUE5eAAAACXBIWXMAAAsSAAALEgHS3X78AAAC3ElEQVQ4y62T3WtTZxzHc1V8A8W0eWkb11fbKgSG4IqIIF7MbX1B3Bj7P3ah0onaMZht04KgF75sjVRjre3uxE1ElIEMHXTUwWzr0jbNy0lyTnKSNCE5OZ/9zkmnXnjpA9/zfc73eZ4v39/vnMfBBx6OarVKKHSHkZFRAuMTBALjNYz/zxOMCY+NBd7BGIGLwzWMfM/oj8NcvjSKpqZxGJUKnx7/HJenGW/TRza7PE14G5txey1tDw3uRpwNHpyuRuobXDg9nWz3f8c2/xA7/KfZuu9bfIfPsRRWcFTKZU5+9TUfH+gVfEJ7Zw8dgvaOLlrba2iz53uFu2lr76Cl6yDOvj/YPTBPff8Ldva/xPfNEkvRihhKwsHBE7S0ddK9z09n134x7RbDvW8MLTNr3Z539NDW6sN76AK7+57jHPiTXZ89o+XkU5bXN2oJvxj4Eu+ebpp8rbilXI+U6mn0SclvYbWiwd2Ey+2hvtnPtoNXqDv6hC1H7lPXG8J9LMjiqmZ9FIOHj3/nztwDgrfnuBGcJjh1l9D0PSZv3SMomJyaEZ7hp5vT/HwzxI3bv3J1bpFrs39xffYlV2fmmfxlnmyuiMP61PqGQSxdQs2bZDZALUBaOJaDiG4SzQmyEBc9IbpShGS+giL7kwVDdAO1WBEns2a4nlBZeBXhdURlJZYhLKf/DqtE1RKKXiWeKbMYybKqFEhkDRvL4r60rstahVhGAslewzAsQ5OUxFpT8kSUHGsJnWiqIKaqzLOsJ/MoYhhN5WRdt99j6SIpXRJmSsJlUjkDrVDF+qdtQ0VqDMd0VuM6K/HNQ5qYaHIoW0MkVWItVWZVYKX+J5zk2fxrni+EebHwL6/CcUxzs2Q1kyMcUUhqBdLZIppeJJlWpRWa3Q4tVyZfMgXSV0ma0Db47dFThs5e4PzwD5w6c5bpu7O1q2c/xdmUuO8OK76lVW3dfKObpqUZ773HVsL/AKKYG7YRli9uAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Full bleed",
        "title": "Full bleed",
        "src": "/static/65984fdc3f376186710762979dc41596/fb070/Full_bleed.png",
        "srcSet": ["/static/65984fdc3f376186710762979dc41596/d6747/Full_bleed.png 288w", "/static/65984fdc3f376186710762979dc41596/09548/Full_bleed.png 576w", "/static/65984fdc3f376186710762979dc41596/fb070/Full_bleed.png 1152w", "/static/65984fdc3f376186710762979dc41596/fb104/Full_bleed.png 1728w", "/static/65984fdc3f376186710762979dc41596/8fefe/Full_bleed.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <h3 {...{
      "id": "aspect-ratios"
    }}>{`Aspect ratios`}</h3>
    <p>{`When designing fluid layouts, it’s helpful for an asset or card to be a specific aspect ratio. We offer several classes for specifying aspect ratio for given content. We use these classes to maintain consistency throughout our UI.`}</p>
    <p>{`We discuss aspect ratios and various grid behaviors more in depth on the 2x Grid Basics tab, both in the `}<a parentName="p" {...{
        "href": "/guidelines/2x-grid/basics#aspect-ratio"
      }}>{`Aspect ratios`}</a>{` section and in the `}<a parentName="p" {...{
        "href": "/guidelines/2x-grid/basics#grid-behaviors"
      }}>{`Grid behaviors`}</a>{` section.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` As of now, the 3:2 aspect ratio is not included in the grid package but there is an issue to add it.`}</p>
    </InlineNotification>
    <Row mdxType="Row">
  <Column noGutterSm mdxType="Column">
    <div className="grid-demo aspect-ratio-demo">
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--2x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">2:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--2x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">2:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--2x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">2:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--2x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">2:1</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--16x9">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">16:9</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--16x9">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">16:9</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--16x9">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">16:9</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--16x9">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">16:9</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col">
             <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--4x3">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">4:3</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
             <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--4x3">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">4:3</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
             <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--4x3">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">4:3</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--4x3">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">4:3</div>
              </div>
            </div>
          </div>
        </div>
      </div>
       <div className="bx-demo--grid">
        <div className="bx-demo--row">
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--1x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">1:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--1x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">1:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--1x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">1:1</div>
              </div>
            </div>
          </div>
          <div className="bx-demo--col">
            <div className="bx-demo--aspect-ratio bx-demo--aspect-ratio--1x1">
              <div className="bx-demo--aspect-ratio--object">
                <div className="aspect-ratio-content">1:1</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
        <pre><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<div class="bx--aspect-ratio bx--aspect-ratio--2x1">
  <div class="bx--aspect-ratio--object">2x1</div>
</div>

<div class="bx--aspect-ratio bx--aspect-ratio--16x9">
  <div class="bx--aspect-ratio--object">16:19</div>
</div>

<div class="bx--aspect-ratio bx--aspect-ratio--4x3">
  <div class="bx--aspect-ratio--object">4:3</div>
</div>

<div class="bx--aspect-ratio bx--aspect-ratio--1x1">
  <div class="bx--aspect-ratio--object">1:1</div>
</div>
`}</code></pre>
  </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "basic-usage"
    }}>{`Basic usage`}</h4>
    <p>{`We understand that when using responsive grid classes, it’s not always possible for every image or container to be one of these sizes. However it’s important to use them to set the foundation of your layouts, especially in the case of same size tiles.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "59.19642857142857%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAACiUlEQVQoz62RT0jTYRjHdyrB/RGTIDxa2KVQKUToEBVkuDAPHYQOgSAeQoLFxFRYzpJZOc2VJkSQa7pNxD8DNQsxIhU1ZG1qmNq0HWRz03Tqptunn+/As0EvfPk+z3v4PO/3fWT85yOLxWK0tLSg1WrR6R5RXV0juY6qqioqKyvRaDSYzWbm5+exWCxYOzp4L/XtlnbhVqsVm9VOV1cX6+vryPb398nJyUEmk6FKUqJUKTiecEz0CQkJwtVqNRsbGwSDAbTlFSSdPEVW1nUyzl+lzzbOknMTz68VIpEIsmg0Sm5uLikpKVQ8eMKrZ2ZePm3joeYx6enpJCcnU1hYyN7enojUYGrm4qUrqG8WkK/OZ2zAw6obtv7sEItF48Ds7GzxkpG+WcJehD5LtUqlEvd5eXkC6Pf7CQQCbG1usrbmlyIGWJjYwflhG99qkP3oXhxoMBgoKSmhp3OQr8PTQt32AUpLSykuLsZkMv3bUkZHR3E4HNg7bbRbLdLnt2GzddDfP0BPTy9Op5PJyUn0er0YXldXd+i1tbXSIqul2oDP54sDvV4vi4uLuGfmcLnn+O6a4efiEiu/PdJ2fxAOh2lsbBTxlUrloRITEyXJUSpU0l+fwOVyxYG7u7tCUyP9DHe9xT32Cf+CG78H1ry7IkpzczNyuZzMjExuF9wi7XSaGFB89z4xH+grjExMjseBodAWgWCQ3jcGmrR3sNTe411THZ7ZEKvLmwJoNBoFIDU1lbPpZ1AoFKLPOHcBfeULrl2+wdS3qTjwIFIotM2go5vpL4OMfezFbrMTjuwQ2t4SwKGhIYqKiigrK0dXU8/r1lbqpSENxnocHWbJn+NZXkZ2lM0dDD3q+QtiUdsoc4phOQAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Aspect ratio",
        "title": "Aspect ratio",
        "src": "/static/ef4badccac786880266c7db65fb28836/fb070/Aspect_Ratio.png",
        "srcSet": ["/static/ef4badccac786880266c7db65fb28836/d6747/Aspect_Ratio.png 288w", "/static/ef4badccac786880266c7db65fb28836/09548/Aspect_Ratio.png 576w", "/static/ef4badccac786880266c7db65fb28836/fb070/Aspect_Ratio.png 1152w", "/static/ef4badccac786880266c7db65fb28836/fb104/Aspect_Ratio.png 1728w", "/static/ef4badccac786880266c7db65fb28836/8fefe/Aspect_Ratio.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <br />
    <h4 {...{
      "id": "adaptability"
    }}>{`Adaptability`}</h4>
    <p>{`Image size can change ratios when the breakpoint changes.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "59.19642857142857%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAMCAYAAABiDJ37AAAACXBIWXMAAAsSAAALEgHS3X78AAACg0lEQVQoz62TS0xTQRhG2bCjrRYKGB4migoY48YYNWpIA8aIbmBHfGw0cSkbJQE0YC0goFTYGDSxWOiGjSJKcGGIiQjKo7S3tw9LkVpLeVSB8rApHC+XCMrCuHCSL5N/cubMzJ9MzOrqKn8bKysr/OtYc8X82tT1qguj0YjpqYmWlhbaWtsIhUIyODw8jPGJUV5rNbXKMZlM67PEm83mDVYWRiIRtFotKqUKtVqNUqkkKSkZq9UqQ2VlZcTGxpKQkIAiToFCoZAZlUpFXFwcGo0Gi8XypzDvVC7JO5JI35nKtu1KKSoEQZChiooK4uPVZGdnkZ6eRmpaKppEDYlS4hPiSUlN2ThcFi4tLVGnb6SytBZdeR03b+gpKa5AtDtlqLqqhj27Mzl25ASHDx1lf9ZBud6bkUXGrn1kZx6QhLZNYXh+AXHQx1wQpnwRQgHw2mbxuMdkaKBXwGWZ4OvoHD73d7z2KewSvxanxU9/j4jL7v1NGF7gw1sBjziD3RJAsExKtQe3Y11o+ejE55knFIwyHYgwE/iBfyzMF28Y39gSwpAfl7BFOPjOwWf3HKJ1GoftG0O943hc68LBPhGHdQafd5HA+CKj4iSvO/vo6uij89l7nrf34BjZIhzqdeGXbuEVQ4y7Z7H1+zeeLI6MSm2Isji71nAYE/1SrxvQ3zJIfa/nnv4hn5zjm8Ll5WUunb9Mbs4ZTmnzyTmeh/bkaQSbXYbMpg5KSx5TV99NbdMA5ZXtFOQXUXjuIgVnL1BUeAXbiGNTGI1GaX7UzG1dJdXVVeju6Ki5W81EMChDL16+4eq1Jq7ruim+76bU0MODhnoMhkYMDQYapAQCgXXh//56PwEIxPojnDovuAAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Aspect ratio flexible",
        "title": "Aspect ratio flexible",
        "src": "/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/fb070/Aspect_Ratio_flexible.png",
        "srcSet": ["/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/d6747/Aspect_Ratio_flexible.png 288w", "/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/09548/Aspect_Ratio_flexible.png 576w", "/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/fb070/Aspect_Ratio_flexible.png 1152w", "/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/fb104/Aspect_Ratio_flexible.png 1728w", "/static/b36f26cd7b46bb8ba5cc516bb59ce8bf/8fefe/Aspect_Ratio_flexible.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="When combining tiles, or tiles and images, do give each a designated aspect ratio." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100.18382352941175%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAB1UlEQVQ4y7WU227aQBRF+f8PaGreMDzCPyClBJw2QooSCJeE2PENDC0XG+wxnt2ZM3JouTSV6o605DMea2kfH3AJQFQkJRS8pJAXtaSrpK7FLOkq5UWWZf9EHoyEjDE0Gg3ouo5arYZq9e+Qz1YqOur1OuI4PgiTJBEHFWiadqD8+Xe0U8plDVdXn6BXdex2u4NQRu50Omg2mzAMg7j5YqB1fUvIun1zi3ZL0ZE18ZXO7r51kaapEua9B0EA27Yp+na7hT/cw+sBXh9wexnmr8BsqFiYwPxF1dMBsPRpEpCq96FEUYQwjLBer7FcruAOEzy3OV4MDrvP4A5S2A+c8EYp3Kc91W/3HIHFhC6TzoNws9lgtVq/X6XQ6oJwhFBKnEcQ/likH2RU2w8i7Vt6KgzDkNLlQruXwLwDzC6Hdc+ERKTqyfY5ps8pvRJZO48cC5t9LHRHMYbXGUatTLScKGGfE7NJSilpL6QL50LLUpjjT3ZwnpgggTeOEZhK6ot0gZVg9spUUnHvuxefCtVQQiKKQtHGHtOxmKRgboopm2I/AjG39ggmGZ1PxaR/eL+8w8v/ZX4EjuqjPacfDj74OPxBcEmYt3wenNnj/DPKiP/ygS10/QRn0fAn62qtyAAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "When combining tiles, or tiles and images, do give each a designated aspect ratio.",
              "title": "When combining tiles, or tiles and images, do give each a designated aspect ratio.",
              "src": "/static/374e7e4edb053be9610c9d952e208c9f/9eefd/AR_do_1.png",
              "srcSet": ["/static/374e7e4edb053be9610c9d952e208c9f/d6747/AR_do_1.png 288w", "/static/374e7e4edb053be9610c9d952e208c9f/09548/AR_do_1.png 576w", "/static/374e7e4edb053be9610c9d952e208c9f/9eefd/AR_do_1.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not shoe-horn each tile / tile and image into a combined aspect ratio." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABe0lEQVQ4y82UW2vCQBCF/f+/QAzURnzSPyEqVhH0yQfvmqgJEcT7XU97hk5isLRQttDA5+64OydndtgkYPhJ/Ing/X43ggjqxMRDrdDh9XrF+Xz+FZfLJXLIH/6Zz+eRTqdh23bI6yf2N7x85ORyORyPx0jwdDohk8kglUqFWJYV43HtcU8ymUQ2m8XhcIgEb7cbarUaCoUC6vW6UC6XUSwWhVKpJOvValV4nL9VKmg0GlJ2rCmLxQKz2Uys7/d7OI6D0WiE8XiMwWCA6XQqMeE+jYfDIYIgeG7KdrsV1us1lssl+v0+Wq2W0Ol0RJQjoZDG7XYbk8nkWXCz2WC1WoUjk1hKs9lEr9eTmCOha4273a44/tIh3akgHRJ1QgEmEwqyVM659qMgRybRAUWZzCTXdQXP88KYZ61nGLt6KqSi3EwHWi4T1bU2jHOu8wVPDtnZ3W4X4vu+uGQyO0pHjAnnbISe5Xw+jwRN3eXY1TP1pYmVbFTwX3+x3wEWd+Y9OcgOmgAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not shoe-horn each tile / tile and image into a combined aspect ratio.",
              "title": "Do not shoe-horn each tile / tile and image into a combined aspect ratio.",
              "src": "/static/4028bb81df9cb9aa343b5e2f02e802b6/9eefd/AR_dont_1.png",
              "srcSet": ["/static/4028bb81df9cb9aa343b5e2f02e802b6/d6747/AR_dont_1.png 288w", "/static/4028bb81df9cb9aa343b5e2f02e802b6/09548/AR_dont_1.png 576w", "/static/4028bb81df9cb9aa343b5e2f02e802b6/9eefd/AR_dont_1.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample type="do" caption="Do use a designated aspect ratio for a grid of same-size fixed ratio tiles." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABuElEQVQ4y82U2VLCQBBF+f8PQOOTBB7hL1TAtdxBQASCSUhA3sgymWt3ZwKW+uCCVaZykz7VPXc6M0lK2PJR+hNDrfVWJIZFsI2DvdYdZln2K6075EuSJGg0GrBtG7VaDdXq18S1lYqNer2OKIo2hnEcY7+yj53dMqw9C5a1+0VZKO+UYVdtrFarjaFSCqcn5zg6aKN5eIzm0TFaJIk/4ZbhduuEuI2z0wukaVpsiqwiXqaAfw/MukD4CMxHxB0S85D0ZLiT54I+MGwC4zNg6eYe7FWiO50ZwkmM6Z3Cc0fB6yfwhzGe75XIGxCTCuac20swaCmMLhRCJxYPbAw1Fm5Mg1L4jymCUSITCJOCcSwq8pzjmmCU88J7ZwiC4Elhcgk414DbyahLBefK8EMmEiZ5PSU1HPOYkIw/dkiz8GyzYd5R6EQSs7gj1pqdvGOOecyLH33SIc3iXGtMbzXcLq9biumNFnm9VLRmynEN1/KYcJy8N9RYegqzAe0e7XA4zrBwFALD80kmKphzc6rhmMcsfSVPKYZFYL5GvIHvsVm6knmDzB8DRj/j/EvRxUv5O0MUj/zv/9ivwaXwMz/kSdgAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do use a designated aspect ratio for a grid of same-size fixed ratio tiles.",
              "title": "Do use a designated aspect ratio for a grid of same-size fixed ratio tiles.",
              "src": "/static/24ed9ef69badab6c37c3b308958f6ed9/9eefd/AR_do_2.png",
              "srcSet": ["/static/24ed9ef69badab6c37c3b308958f6ed9/d6747/AR_do_2.png 288w", "/static/24ed9ef69badab6c37c3b308958f6ed9/09548/AR_do_2.png 576w", "/static/24ed9ef69badab6c37c3b308958f6ed9/9eefd/AR_do_2.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={4} mdxType="Column">
  <DoDontExample caption="Do not start with a random aspect ratio for a grid of same-size fixed ratio tiles." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABXElEQVQ4y82UzY6CQBCEff8X8OcGeNS3UDzoSTERRRH/jdxBRXvzTdJjNuwmJovJkhSpGrqL6plATSq+ah8xfD6flcAYKqniwssmfDwef4JNyO12u0m32xXXdaXdbovneW+BWsdxpNPpSJ7nL8Pr9WoeNBoNabVa0mw23wK19XrdmGdZ9jIsikKGw6H0ej3xfd9gMBhY/huo6ff7MhqN5H6/fz+U4/Eo8/lcFouFxHEsu93O6vV6LZvNxnAAZ202mxmcTqfXoahhkiQyHo8lCAJTtFwuZTKZGB2GoTFSrcYkJN12uy0b8pbVamXSkWC/31tNA4Brek2J/jEhRSQjDel0JLQaq4ZTA2eN3pIhb2Sk6XRqTRkPzXhRFFkNZ3/hrNFbMjwcDraRdIwIB+wvUK3jwumht2R4uVxMdMCJn89nq9kjoJpn1KhO07RsWPm3XPnfpgozm/Bf/7G/ABrC3lxjHVRDAAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Don’t start with a random aspect ratio for a grid of same-size fixed ratio tiles.",
              "title": "Don’t start with a random aspect ratio for a grid of same-size fixed ratio tiles.",
              "src": "/static/cd10ca7219a3e3b5c4df4fd8a6ef2e94/9eefd/AR_dont_2.png",
              "srcSet": ["/static/cd10ca7219a3e3b5c4df4fd8a6ef2e94/d6747/AR_dont_2.png 288w", "/static/cd10ca7219a3e3b5c4df4fd8a6ef2e94/09548/AR_dont_2.png 576w", "/static/cd10ca7219a3e3b5c4df4fd8a6ef2e94/9eefd/AR_dont_2.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      